import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';
import { useReviewQueueStore } from '@/stores/reviewQueue';
import type { ResolutionResultData } from '@/services/api';

const DECISION_COLORS: Record<string, string> = {
  merge: 'bg-emerald-400',
  link: 'bg-blue-400',
  distinct: 'bg-slate-300',
  promote: 'bg-violet-400',
};

export function ReviewQueueList() {
  const { results, selectedId, loading, selectResult } = useReviewQueueStore();

  if (loading && results.length === 0) {
    return (
      <div className="text-sm text-slate-400 text-center py-8">
        <Loader2 size={14} className="animate-spin inline mr-2" />
        Loading queue...
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-sm text-slate-400 text-center py-12">
        <p>No items in the queue for the current filters.</p>
        <p className="text-[10px] mt-1">
          Toggle "Show reviewed" to see past verdicts.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
      {results.map((r) => (
        <Row
          key={r.id}
          result={r}
          selected={selectedId === r.id}
          onClick={() => selectResult(r.id)}
        />
      ))}
    </div>
  );
}

function Row({
  result,
  selected,
  onClick,
}: {
  result: ResolutionResultData;
  selected: boolean;
  onClick: () => void;
}) {
  const dot = DECISION_COLORS[result.decision] ?? 'bg-slate-300';
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-4 py-3 transition-colors',
        selected
          ? 'bg-brand-teal/5 border-l-4 border-l-brand-teal'
          : 'hover:bg-slate-50 border-l-4 border-l-transparent',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', dot)} />
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border bg-white border-slate-200 text-slate-700 flex-shrink-0">
          {result.decision}
        </span>
        <span className="text-sm text-slate-800 truncate">
          {result.source_entity_ids.slice(0, 2).map((s) => s.slice(0, 8)).join(', ')}
          {result.source_entity_ids.length > 2 && ' +more'}
        </span>
        <span className="ml-auto text-[10px] text-emerald-600 font-semibold flex-shrink-0">
          {(result.confidence * 100).toFixed(0)}%
        </span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 pl-3.5">
        {result.entity_type && (
          <span className="truncate max-w-[10rem]">{result.entity_type}</span>
        )}
        {result.human_verdict && (
          <span
            className={clsx(
              'font-bold uppercase',
              result.human_verdict === 'confirmed' && 'text-emerald-600',
              result.human_verdict === 'overridden' && 'text-rose-600',
              result.human_verdict === 'flagged' && 'text-amber-600',
            )}
          >
            {result.human_verdict}
          </span>
        )}
        {result.human_reviewed_by && (
          <span className="truncate max-w-[10rem]">
            by {result.human_reviewed_by}
          </span>
        )}
        {result.reasoning && (
          <span className="truncate max-w-[20rem] italic text-slate-500">
            {result.reasoning.slice(0, 120)}
            {result.reasoning.length > 120 ? '…' : ''}
          </span>
        )}
      </div>
    </button>
  );
}
